import { Extent } from "ol/extent";
import {
  ListGroup,
  ListItem,
} from "@avinet/adaptive-ui-core/form/controls/List";

export enum DisplaySectionType {
  heading = "display_heading",
  text = "display_text",
  image = "display_image",
}

export enum InputSectionType {
  text = "text",
  text_area = "text_area",
  email = "email",
  url = "url",
  integer = "integer",
  decimal = "decimal",
  boolean = "boolean",
  date = "date",
  list = "list",
  multiselect_list = "multiselect_list",
  media = "media",
  rating = "rating",
}

export enum GeometrySectionType {
  geometry_point = "geometry_point",
  geometry_line = "geometry_line",
  geometry_polygon = "geometry_polygon",
}

/** Only used to preserve columns added behind the scenes for now. */
export enum HiddenSectionType {
  foreign_key = "foreign_key",
  datetime = "datetime",
  uuid = "uuid",
  dictionary = "dictionary",
  multiselect_dictionary = "multiselect_dictionary",
}

export const sectionGroups: ListGroup[] = [
  { group: "display" },
  { group: "geometry" },
  { group: "input" },
];

export const sectionTypeGroups: ListItem[] = [
  ...Object.values(sectionGroups).map((value) => ({ value: value.group })),
];

export const sectionTypes: ListItem[] = [
  ...Object.values(DisplaySectionType).map((value) => ({
    value,
    group: "display",
  })),
  ...Object.values(InputSectionType).map((value) => ({
    value,
    group: "input",
  })),
  ...Object.values(GeometrySectionType).map((value) => ({
    value,
    group: "geometry",
  })),
];

export interface DisplaySection {
  id: string;
  type: DisplaySectionType;
  content: string;
}

export interface StoredSection {
  type: InputSectionType | GeometrySectionType | HiddenSectionType;
  id: string;
  columnName: string;
  label: string;
  helpText: string;
  required: boolean;
  administrative?: boolean;
}

export function isStoredSection(section: any): section is StoredSection {
  if (!section.type) return false;
  return (
    section.type in InputSectionType || section.type in GeometrySectionType
  );
}

interface BaseInputSection extends StoredSection {
  type: InputSectionType;
}

export interface BasicInputSection extends BaseInputSection {
  type:
    | InputSectionType.boolean
    | InputSectionType.date
    | InputSectionType.email
    | InputSectionType.url
    | InputSectionType.text_area
    | InputSectionType.text;
}

export interface RatingInputSection extends BaseInputSection {
  type: InputSectionType.rating;
  max?: number;
  symbol: "heart" | "star" | "smiley";
}

export interface IntegerInputSection extends BaseInputSection {
  type: InputSectionType.integer;
  min?: number;
  max?: number;
}

export interface DecimalInputSection extends BaseInputSection {
  type: InputSectionType.decimal;
  min?: number;
  max?: number;
  decimals?: number;
}

export interface ListInputSection extends BaseInputSection {
  type: InputSectionType.list | InputSectionType.multiselect_list;
  items?: ListItem[];
}

export interface MediaInputSection extends BaseInputSection {
  type: InputSectionType.media;
  accept: "image" | "document";
}

export interface GeometrySection extends StoredSection {
  type: GeometrySectionType;
  extent: Extent;
  basemap: string;
  multi: boolean;
}

export interface HiddenSection extends StoredSection {
  type: HiddenSectionType;
}

export function isGeometrySection(
  section: Section
): section is GeometrySection {
  if (section.type in GeometrySectionType) return true;
  return false;
}

export type InputSection =
  | BasicInputSection
  | RatingInputSection
  | IntegerInputSection
  | DecimalInputSection
  | ListInputSection
  | MediaInputSection;

type Section =
  | BasicInputSection
  | RatingInputSection
  | IntegerInputSection
  | DecimalInputSection
  | ListInputSection
  | MediaInputSection
  | DisplaySection
  | GeometrySection
  | HiddenSection;

export type SectionType =
  | keyof typeof DisplaySectionType
  | keyof typeof InputSectionType
  | keyof typeof GeometrySectionType
  | keyof typeof HiddenSectionType;

export default Section;
